import { useCallback, useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import GetAppIcon from '@mui/icons-material/GetApp'
import RefreshIcon from '@mui/icons-material/Refresh'
import PublicAppHeader from 'src/consumer/components/PublicAppHeader'
import { APP_CONSTANTS } from 'src/consumer/constants'
import { SEOComponent } from 'src/consumer/helpers/utilFunctions'
import { loadPayoutReportPdf } from 'src/consumer/services/payoutReportViewerService'
import { toast } from 'react-toastify'

function getPayoutReportErrorMessage(status) {
  if (status === 404) {
    return "We couldn't find this payout report."
  }
  if (status === 410) {
    return 'This payout report link is no longer active.'
  }
  return 'Something went wrong. Please try again.'
}

const DEFAULT_PAYOUT_REPORT_FILENAME = 'payout-report.pdf'

export default function PayoutReportViewerPage() {
  const { invoiceId } = useParams()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const pdfContainerRef = useRef(null)

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null)

  const fetchPdf = useCallback(async () => {
    if (!invoiceId) {
      setError('Invalid payout report link.')
      setLoading(false)
      return
    }
    setLoading(true)
    setError(null)
    setPdfBlobUrl((prev) => {
      if (prev && typeof URL !== 'undefined' && typeof URL.revokeObjectURL === 'function') {
        URL.revokeObjectURL(prev)
      }
      return null
    })
    try {
      const blob = await loadPayoutReportPdf(invoiceId)
      const url = URL.createObjectURL(blob)
      setPdfBlobUrl(url)
      if (isMobile && pdfContainerRef.current) {
        pdfContainerRef.current.scrollIntoView({ behavior: 'smooth' })
      }
    } catch (res) {
      const status = res?.status ?? (res?.response?.status ?? null)
      setError(getPayoutReportErrorMessage(status))
    } finally {
      setLoading(false)
    }
  }, [invoiceId, isMobile])

  useEffect(() => {
    fetchPdf()
  }, [fetchPdf])

  useEffect(() => {
    return () => {
      if (pdfBlobUrl && typeof URL !== 'undefined' && typeof URL.revokeObjectURL === 'function') {
        URL.revokeObjectURL(pdfBlobUrl)
      }
    }
  }, [pdfBlobUrl])

  const handleDownload = () => {
    if (!pdfBlobUrl) return
    const a = document.createElement('a')
    a.href = pdfBlobUrl
    a.download = DEFAULT_PAYOUT_REPORT_FILENAME
    a.rel = 'noopener noreferrer'
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    toast.success('Download started.')
  }

  if (loading) {
    return (
      <>
        <PublicAppHeader />
        <SEOComponent
          title="View payout report | AUMHUM"
          description="View your payout report"
          image={APP_CONSTANTS.APP_LOGO_LINK}
        />
        <Box
          sx={{
            minHeight: '70vh',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            p: 3,
            background: 'linear-gradient(180deg, rgba(248,250,252,0.6) 0%, transparent 100%)',
          }}
        >
          <CircularProgress size={40} thickness={4} sx={{ color: 'primary.main', mb: 2 }} />
          <Typography variant="body2" color="text.secondary">
            Loading payout report...
          </Typography>
        </Box>
      </>
    )
  }

  if (error && !pdfBlobUrl) {
    return (
      <>
        <PublicAppHeader />
        <SEOComponent
          title="Payout report | AUMHUM"
          description="View your payout report"
          image={APP_CONSTANTS.APP_LOGO_LINK}
        />
        <Box
          sx={{
            minHeight: '60vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 3,
          }}
        >
          <Paper
            elevation={0}
            sx={{
              maxWidth: 420,
              p: 4,
              textAlign: 'center',
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                bgcolor: 'error.50',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 2,
              }}
            >
              <ErrorOutlineIcon sx={{ fontSize: 32, color: 'error.main' }} />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
              Unable to load payout report
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
              {error}
            </Typography>
            <Button
              variant="contained"
              startIcon={<RefreshIcon />}
              onClick={fetchPdf}
              sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
            >
              Retry
            </Button>
          </Paper>
        </Box>
      </>
    )
  }

  const pdfMinHeight = isMobile ? 420 : 400
  return (
    <>
      <PublicAppHeader />
      <SEOComponent
        title="View payout report | AUMHUM"
        description="View your payout report"
        image={APP_CONSTANTS.APP_LOGO_LINK}
      />
      <Box
        sx={{
          minHeight: '100vh',
          bgcolor: 'grey.50',
          pb: { xs: 4, md: 0 },
          ...(isMobile
            ? {}
            : { height: 'calc(100vh - 64px)', display: 'flex', flexDirection: 'column' }),
        }}
      >
        <Box
          sx={{
            width: '100%',
            maxWidth: 1200,
            mx: 'auto',
            px: { xs: 2, sm: 3, lg: 4 },
            pt: { xs: 2, sm: 3 },
            flex: 1,
            minHeight: 0,
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Typography
            variant="h5"
            sx={{
              fontWeight: 600,
              color: 'text.primary',
              mb: { xs: 2, md: 2 },
              fontSize: { xs: '1.25rem', sm: '1.5rem' },
              flexShrink: 0,
            }}
          >
            Payout report
          </Typography>
          <Box
            ref={pdfContainerRef}
            sx={{
              width: '100%',
              flex: 1,
              minHeight: pdfMinHeight,
              display: 'flex',
              flexDirection: 'column',
              minWidth: 0,
            }}
          >
            <Paper
              elevation={0}
              component="div"
              sx={{
                position: 'relative',
                width: '100%',
                flex: 1,
                minWidth: 0,
                overflow: 'hidden',
                borderRadius: 3,
                border: '1px solid',
                borderColor: 'divider',
                boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                minHeight: pdfMinHeight,
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Box
                sx={{
                  flexShrink: 0,
                  display: 'flex',
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                  px: 1.5,
                  py: 1,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  bgcolor: 'background.paper',
                }}
              >
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<GetAppIcon />}
                  onClick={handleDownload}
                  sx={{ textTransform: 'none', borderRadius: 2 }}
                >
                  Download
                </Button>
              </Box>
              <Box
                sx={{
                  position: 'relative',
                  flex: 1,
                  minHeight: 0,
                }}
              >
                <iframe
                  title="Payout report PDF"
                  src={pdfBlobUrl}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    border: 'none',
                  }}
                />
              </Box>
            </Paper>
          </Box>
        </Box>
      </Box>
    </>
  )
}
